import { MAZE, NIGHTMARES, type EnemyKind } from '@somnhold/content';
import { cellAtWorld, cellCenter, cellId, gridW, heroWalkable, touchGrid } from '../grid.ts';
import { DIRS, hexDist, hexLine, hexToWorld, worldToHex, type Hex } from '../hex.ts';
import { idiv, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, DungeonSpawn, Enemy, Hero, Maze, MazeEdge, MazeLayout } from '../types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';

/*
 * The Furrows (docs/LABERINTOS.md). A run starts at the group's deepest camp (or the way in) with what the group
 * already mapped this week; heroes see crossings near them, pull gates' levers, pitch camps and open chests by
 * standing at them; walls breathe open and shut; packs wake when a hero comes near and chase along the corridors;
 * the prowler hunts whoever it hears and never falls. Back by a rope, when the torch dies, or with the hall's troll.
 */

const M = MAZE;
const standing = (h: Hero): boolean => h.downTicks === 0;
const at = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);
const CYCLE = M.breathOpen + M.breathShut;

/** What the campaign hands a run: the week, its seed, what the group has, and how hard it comes. */
export interface MazeParams {
  week: number;
  seed: number;
  /** The labyrinth's radius (absent: MAZE.radius). */
  radius?: number;
  seen: number[];
  gates: number[];
  camps: number[];
  chests: number[];
  centreWon: boolean;
  /** Party scaling of the prowler's bar and the troll's health (heroes 1 to 4). */
  party: number;
  guardianHp: number;
}

/** The titan's breath at a tick for breathing wall `i` (by door index): open or shut. */
const breathAt = (tick: number, L: MazeLayout, i: number): boolean => {
  const e = L.edges[L.doors[i] as number] as MazeEdge;
  return (tick + e.phase) % CYCLE < M.breathOpen;
};

/** Is map door `i` open now? Gates by their levers; breathing walls as they stand (see `breathe`). */
export const mazeDoorOpen = (m: Maze, i: number): boolean => {
  const L = m.layout;
  if (i < L.levers.length) return m.gates[i] ?? false;
  return m.breath[i - L.levers.length] ?? true;
};

/** The ropes: the way in, and every camp pitched. */
const ropes = (m: Maze): number[] => [m.layout.entry, ...m.layout.camps.filter((_, i) => m.camps[i])];

/** Where a run starts: the pitched camp deepest in, or the way in. */
const startOf = (L: MazeLayout, camps: boolean[]): number => {
  let best = L.entry;
  L.camps.forEach((c, i) => {
    if (camps[i] && (L.depth[c] as number) > (L.depth[best] as number)) best = c;
  });
  return best;
};

export const newMaze = (s: BattleState, layout: MazeLayout, p: MazeParams, spawns: DungeonSpawn[]): Maze => {
  const L = layout;
  const flag = (n: number, list: number[]): boolean[] => Array.from({ length: n }, (_, i) => list.includes(i));
  const camps = flag(L.camps.length, p.camps);
  const packHome = [...L.packs, ...L.chests.filter((_, i) => L.guarded[i])];
  const m: Maze = {
    week: p.week,
    layout: L,
    seen: flag(L.nodes.length, p.seen),
    gates: flag(L.levers.length, p.gates),
    camps,
    chests: flag(L.chests.length, p.chests),
    openedBy: L.chests.map(() => ''),
    centreWon: p.centreWon,
    leverHold: L.levers.map(() => 0),
    campHold: L.camps.map(() => 0),
    chestHold: L.chests.map(() => 0),
    breath: L.doors.slice(L.levers.length).map((_, k) => breathAt(s.tick, L, L.levers.length + k)),
    torchAt: 0,
    packHome,
    packWoke: packHome.map(() => false),
    armed: false,
    prowlers: [],
    guardian: 0,
    hallWoke: false,
    start: startOf(L, camps),
    falls: 0,
    downAt: s.heroes.map(() => 0),
    outcome: '',
    ok: {},
    spawns,
  };
  // A guarded chest already opened has nobody left by it.
  L.chests.forEach((_, i) => {
    if (!L.guarded[i] || !m.chests[i]) return;
    const k = L.packs.length + L.chests.slice(0, i).filter((__, j) => L.guarded[j]).length;
    m.packWoke[k] = true;
  });
  const c = at(L.nodes[m.start] as Hex);
  s.heroes.forEach((h, i) => {
    h.x = h.destX = h.ax = c.x + ((i % 2) * 2 - 1) * 250;
    h.y = h.destY = h.ay = c.y + (i < 2 ? -250 : 250);
    h.path = [];
    h.moving = false;
  });
  // The prowlers start where the party is not: the crossings farthest from the start.
  const far = L.nodes
    .map((_, i) => i)
    .filter((i) => i !== L.hall)
    .sort((a, b) => hexDist(L.nodes[b] as Hex, L.nodes[m.start] as Hex) - hexDist(L.nodes[a] as Hex, L.nodes[m.start] as Hex) || a - b);
  const count = M.prowler.count[p.party] ?? 1;
  for (let k = 0; k < count; k++) {
    const home = far[k * 7] ?? far[0] ?? L.entry;
    const w = at(L.nodes[home] as Hex);
    const e = spawnEnemy(s, 'prowler', 1, false, 0, w.x, w.y, M.prowler.staggerHp[p.party] ?? M.prowler.staggerHp[1]);
    if (s.dungeon) e.damage = mulBp(e.damage, s.dungeon.dmgBp);
    m.prowlers.push({ id: e.id, goal: home, awayUntil: 0 });
  }
  if (s.dungeon) s.dungeon.bossHp = p.guardianHp;
  return m;
};

/** Before the run starts, the party waits at its start. */
export const holdAtStart = (s: BattleState, m: Maze): void => {
  const c = at(m.layout.nodes[m.start] as Hex);
  for (const h of s.heroes) {
    if (withinRange(h.x, h.y, c.x, c.y, 700)) continue;
    h.x = h.destX = c.x;
    h.y = h.destY = c.y;
    h.path = [];
    h.moving = false;
  }
};

/** A cell the group has mapped: near a crossing it has seen. */
export const mazeKnows = (m: Maze, q: number, r: number): boolean =>
  m.layout.nodes.some((n, i) => m.seen[i] && hexDist(n, { q, r }) <= (i === m.layout.hall ? 3 : 1));

/** A cell a hero can be sent to: mapped, or lit by a standing hero's torch. */
export const mazeKnowsAt = (s: BattleState, m: Maze, q: number, r: number): boolean =>
  s.heroes.some((h) => standing(h) && hexDist(worldToHex(h.x, h.y), { q, r }) <= M.torchCells) || mazeKnows(m, q, r);

/**
 * Where an order into the dark takes the hero: as far as the light reaches toward it (the last known floor cell on
 * the line there). Null when the target is known already.
 */
export const mazeReach = (s: BattleState, m: Maze, h: { x: number; y: number }, x: number, y: number): { x: number; y: number } | null => {
  const t = worldToHex(x, y);
  if (mazeKnowsAt(s, m, t.q, t.r)) return null;
  const from = worldToHex(h.x, h.y);
  let best = from;
  for (const c of hexLine(from, t)) {
    if (!mazeKnowsAt(s, m, c.q, c.r)) break;
    if (heroWalkable(s, cellId(s.map.radius, c.q, c.r))) best = c;
  }
  return at(best);
};

// ---------- the corridors as a field ----------

/**
 * Corridor steps (cells) from every walkable cell to the nearest of `from`, through open doors only. Recomputed
 * whenever it is asked (it is cheap: a few hundred cells), so nothing outside the battle state is needed.
 */
/** The heroes' field, once per tick (heroes don't move while nightmares act): a pure function of the state. */
const heroField = new WeakMap<BattleState, { tick: number; rev: number; d: Int32Array }>();
const heroesField = (s: BattleState): Int32Array => {
  const c = heroField.get(s);
  if (c && c.tick === s.tick && c.rev === s.gridRev) return c.d;
  const d = field(
    s,
    s.heroes.filter(standing).map((h) => cellAtWorld(s, h.x, h.y)),
  );
  heroField.set(s, { tick: s.tick, rev: s.gridRev, d });
  return d;
};

export const field = (s: BattleState, from: number[]): Int32Array => {
  const R = s.map.radius;
  const W = gridW(R);
  const d = new Int32Array(W * W).fill(-1);
  const q: number[] = [];
  for (const id of from) {
    if (id < 0 || d[id] !== -1) continue;
    d[id] = 0;
    q.push(id);
  }
  for (let i = 0; i < q.length; i++) {
    const id = q[i] as number;
    const cq = (id % W) - R;
    const cr = idiv(id, W) - R;
    for (const dd of DIRS) {
      const n = cellId(R, cq + dd.q, cr + dd.r);
      if (n < 0 || d[n] !== -1 || !heroWalkable(s, n)) continue;
      d[n] = (d[id] as number) + 1;
      q.push(n);
    }
  }
  return d;
};

/** One step of `e` down the field (toward its sources); straight at `tgt` once it shares its cell. */
const descend = (s: BattleState, e: Enemy, d: Int32Array, px: number, tgt?: { x: number; y: number }): void => {
  const R = s.map.radius;
  const here = cellAtWorld(s, e.x, e.y);
  const dh = here >= 0 ? (d[here] as number) : -1;
  if (dh === 0 && tgt) {
    stepToward(e, tgt.x, tgt.y, px);
    return;
  }
  const hx = worldToHex(e.x, e.y);
  let best = -1;
  let bd = dh < 0 ? 0x7fffffff : dh;
  for (const dd of DIRS) {
    const n = cellId(R, hx.q + dd.q, hx.r + dd.r);
    if (n < 0) continue;
    const v = d[n] as number;
    if (v >= 0 && v < bd) {
      bd = v;
      best = n;
    }
  }
  if (best < 0) {
    // Already as close as it gets: to the middle of its cell.
    const c = cellCenter(R, here);
    if (here >= 0) stepToward(e, c.x, c.y, px);
    return;
  }
  const c = cellCenter(R, best);
  stepToward(e, c.x, c.y, px);
};

// ---------- the run ----------

/** Nightmares of the run: a pack or a guard (`room` is its pack) or the prowler. */
const spawnPack = (s: BattleState, m: Maze, k: number): void => {
  m.packWoke[k] = true;
  for (const sp of m.spawns) {
    if (sp.room !== k) continue;
    const w = at(sp);
    const e = spawnEnemy(s, sp.kind as EnemyKind, sp.tier, sp.elite, 0, w.x, w.y);
    e.room = k;
    const d = s.dungeon;
    if (d) e.damage = mulBp(e.damage, d.dmgBp);
    if (sp.hpBp && sp.hpBp !== 10000) e.hp = e.maxHp = mulBp(e.maxHp, sp.hpBp);
    ev(s, { t: 'spawn', id: e.id });
  }
};

const nodeCell = (s: BattleState, m: Maze, i: number): number => {
  const n = m.layout.nodes[i] as Hex;
  return cellId(s.map.radius, n.q, n.r);
};

/**
 * Breathing walls follow the titan's breath, but a wall never shuts on someone standing in it: it waits until the
 * way is clear. Those walking through a wall that shuts look for another way.
 */
const breathe = (s: BattleState, m: Maze): void => {
  const L = m.layout;
  let changed = false;
  for (let i = L.levers.length; i < L.doors.length; i++) {
    const k = i - L.levers.length;
    const e = L.edges[L.doors[i] as number] as MazeEdge;
    const cell = cellId(s.map.radius, e.q, e.r);
    const inside = (o: { x: number; y: number }): boolean => cellAtWorld(s, o.x, o.y) === cell;
    const held = s.heroes.some((h) => inside(h)) || s.enemies.some((en) => en.hp > 0 && inside(en));
    const now = breathAt(s.tick, L, i) || (held && (m.breath[k] ?? true));
    if (now === m.breath[k]) continue;
    m.breath[k] = now;
    changed = true;
    ev(s, { t: 'skill', hero: 0, skill: now ? 'mazeBreathOpen' : 'mazeBreathShut', ...at(e) });
    if (now) continue;
    for (const h of s.heroes) {
      if (!h.path.includes(cell)) continue;
      h.path = [];
      if (h.moving) orderMove(s, h, h.destX, h.destY);
    }
  }
  if (changed) touchGrid(s);
};

/** Crossings within sight of a standing hero are seen (the group's for the week). */
const look = (s: BattleState, m: Maze): void => {
  const L = m.layout;
  for (const h of s.heroes) {
    if (!standing(h)) continue;
    const hx = worldToHex(h.x, h.y);
    L.nodes.forEach((n, i) => {
      if (m.seen[i]) return;
      if (hexDist(hx, n) <= M.sightCells + (i === L.hall ? 2 : 0)) {
        m.seen[i] = true;
        ev(s, { t: 'skill', hero: h.id, skill: 'mazeSeen', ...at(n) });
      }
    });
  }
};

/** Standing by something works it: levers, camps, chests. */
const work = (s: BattleState, m: Maze): void => {
  const L = m.layout;
  const near = (i: number): Hero | undefined => {
    const c = at(L.nodes[i] as Hex);
    return s.heroes.find((h) => standing(h) && withinRange(h.x, h.y, c.x, c.y, M.workRange));
  };
  L.levers.forEach((lv, i) => {
    if (m.gates[i]) return;
    m.leverHold[i] = near(lv.node) ? (m.leverHold[i] as number) + 1 : 0;
    if ((m.leverHold[i] as number) < M.leverTicks) return;
    m.gates[i] = true;
    touchGrid(s);
    const e = L.edges[lv.edge] as MazeEdge;
    ev(s, { t: 'skill', hero: 0, skill: 'mazeGate', ...at(e) });
  });
  L.camps.forEach((c, i) => {
    if (m.camps[i]) return;
    m.campHold[i] = near(c) ? (m.campHold[i] as number) + 1 : 0;
    if ((m.campHold[i] as number) < M.campTicks) return;
    m.camps[i] = true;
    ev(s, { t: 'skill', hero: 0, skill: 'mazeCamp', ...at(L.nodes[c] as Hex) });
  });
  L.chests.forEach((c, i) => {
    if (m.chests[i]) return;
    const h = near(c);
    m.chestHold[i] = h ? (m.chestHold[i] as number) + 1 : 0;
    if (!h || (m.chestHold[i] as number) < M.chestTicks) return;
    m.chests[i] = true;
    m.openedBy[i] = h.pid;
    ev(s, { t: 'skill', hero: h.id, skill: 'mazeChest', ...at(L.nodes[c] as Hex) });
  });
};

/** Packs and guards wake when a hero comes near by the corridors; the troll when someone walks into the hall. */
const wake = (s: BattleState, m: Maze, d: Int32Array): void => {
  const L = m.layout;
  m.packHome.forEach((home, k) => {
    if (m.packWoke[k]) return;
    const v = d[nodeCell(s, m, home)] as number;
    if (v >= 0 && v <= M.wakeCrossings * 2) spawnPack(s, m, k);
  });
  if (m.hallWoke || m.centreWon) return;
  if (!s.heroes.some((h) => standing(h) && hexDist(worldToHex(h.x, h.y), { q: 0, r: 0 }) <= 2)) return;
  m.hallWoke = true;
  const w = at({ q: 1, r: 0 });
  const g = M.guardian;
  const e = spawnEnemy(s, g.kind, 2, true, 0, w.x, w.y, s.dungeon?.bossHp ?? 0);
  e.room = -1;
  e.damage = mulBp(mulBp(e.damage, s.dungeon?.dmgBp ?? 10000), g.dmgBp[imin(g.dmgBp.length - 1, s.heroes.length)] as number);
  m.guardian = e.id;
  ev(s, { t: 'spawn', id: e.id });
  ev(s, { t: 'skill', hero: 0, skill: 'mazeHall', ...at(L.nodes[L.hall] as Hex) });
};

/** A prowler that came to after a scare goes away, to the crossing farthest from the party, and keeps off a while. */
const prowl = (s: BattleState, m: Maze, d: Int32Array): void => {
  const L = m.layout;
  for (const p of m.prowlers) {
    const e = s.enemies.find((x) => x.id === p.id);
    if (!e) continue;
    if (e.scared !== undefined && e.stun === 0) {
      let far = p.goal;
      let fd = -1;
      L.nodes.forEach((_, i) => {
        if (i === L.hall) return;
        const v = d[nodeCell(s, m, i)] as number;
        if (v > fd) {
          fd = v;
          far = i;
        }
      });
      const w = at(L.nodes[far] as Hex);
      e.x = w.x;
      e.y = w.y;
      e.scared = undefined;
      p.goal = far;
      p.awayUntil = s.tick + M.prowler.awayTicks;
      ev(s, { t: 'boom', x: w.x, y: w.y, r: 600, kind: 'prowlerAway' });
    }
    // Reached its goal: on to another crossing.
    const g = at(L.nodes[p.goal] as Hex);
    if (withinRange(e.x, e.y, g.x, g.y, 400)) p.goal = rngInt(s.rng, L.nodes.length - 1);
  }
};

const fallTick = (s: BattleState, m: Maze): void => {
  s.heroes.forEach((h, i) => {
    if (h.downTicks === 0) m.downAt[i] = 0;
    else if (m.downAt[i] === 0) {
      m.downAt[i] = s.tick;
      m.falls++;
    }
  });
};

/** Back by a rope (everyone standing on the same one), or the torch dies. */
const ropeTick = (s: BattleState, m: Maze): void => {
  if (m.torchAt === 0) m.torchAt = s.tick + M.torchTicks;
  if (s.tick >= m.torchAt) {
    m.outcome = 'torch';
    return;
  }
  // Once someone has walked away from the ropes, the HUD stops pointing at the way out.
  if (!m.armed) {
    const rs = ropes(m).map((i) => at(m.layout.nodes[i] as Hex));
    m.armed = s.heroes.some((h) => standing(h) && rs.every((r) => !withinRange(h.x, h.y, r.x, r.y, 3000)));
  }
};

/** The rope a hero stands by (its crossing), or -1. */
export const ropeAt = (m: Maze, h: { x: number; y: number }): number => ropes(m).find((i) => withinRange(h.x, h.y, at(m.layout.nodes[i] as Hex).x, at(m.layout.nodes[i] as Hex).y, M.ropeRange)) ?? -1;

/**
 * A hero asks to go back up by the rope at hand: the run ends there if everyone still standing is by it; else the
 * others are told to come.
 */
export const mazeClimb = (s: BattleState, m: Maze, h: Hero): void => {
  if (!standing(h) || m.outcome) return;
  const r = ropeAt(m, h);
  if (r < 0) return;
  const p = at(m.layout.nodes[r] as Hex);
  if (s.heroes.every((o) => !standing(o) || withinRange(o.x, o.y, p.x, p.y, M.ropeGather))) m.outcome = 'back';
  else ev(s, { t: 'skill', hero: h.id, skill: 'mazeClimbWait', ...p });
};

/** One tick of the Furrows, before heroes and nightmares act. */
export const mazeTick = (s: BattleState, m: Maze): void => {
  breathe(s, m);
  look(s, m);
  work(s, m);
  const d = heroesField(s);
  wake(s, m, d);
  prowl(s, m, d);
  fallTick(s, m);
  if (m.hallWoke && m.guardian > 0 && !s.enemies.some((e) => e.id === m.guardian && e.hp > 0)) {
    m.outcome = 'centre';
    m.centreWon = true;
    return;
  }
  ropeTick(s, m);
};

/**
 * A nightmare of the Furrows moves along the corridors: a pack after the nearest hero it can reach, but no farther
 * than its leash; the prowler after whoever it hears, or on to its next crossing; the troll stays in its hall.
 */
export const mazeChase = (s: BattleState, e: Enemy, px: number, attack: (h: Hero) => void): boolean => {
  const m = s.dungeon?.maze;
  if (!m) return false;
  let tgt: Hero | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (!standing(h)) continue;
    if (e.taunter === h.id) {
      tgt = h;
      break;
    }
    const dd = (h.x - e.x) * (h.x - e.x) + (h.y - e.y) * (h.y - e.y);
    if (dd < bd) {
      bd = dd;
      tgt = h;
    }
  }
  if (tgt && withinRange(e.x, e.y, tgt.x, tgt.y, NIGHTMARES[e.kind].reach + 150)) {
    e.tgtKind = 1;
    e.tgtId = tgt.id;
    attack(tgt);
    return true;
  }
  const d = heroesField(s);
  const here = cellAtWorld(s, e.x, e.y);
  const far = here >= 0 ? (d[here] as number) : -1;
  const target = tgt ?? undefined;
  e.anim = 'walk';
  if (e.kind === 'prowler') {
    const p = m.prowlers.find((x) => x.id === e.id);
    // It never goes into the hall: whoever fights the old ogre there is left to it.
    const inHall = !!tgt && hexDist(worldToHex(tgt.x, tgt.y), { q: 0, r: 0 }) <= 2;
    const hunting = !!p && !inHall && p.awayUntil <= s.tick && far >= 0 && far <= M.prowler.hearCrossings * 2;
    if (hunting) descend(s, e, d, px, target);
    else if (p) descend(s, e, field(s, [nodeCell(s, m, p.goal)]), px);
    return true;
  }
  if (e.id === m.guardian) {
    if (far >= 0 && far <= 4) descend(s, e, d, px, target);
    else e.anim = 'idle';
    return true;
  }
  // A pack keeps to its stretch: after a hero within its leash of home, else back home (they never gather).
  const homeNode = m.packHome[e.room ?? -1];
  if (homeNode === undefined) return true;
  const home = m.layout.nodes[homeNode] as Hex;
  const leash = M.leashCrossings * 2;
  const inReach = !!tgt && hexDist(worldToHex(tgt.x, tgt.y), home) <= leash && hexDist(worldToHex(e.x, e.y), home) <= leash;
  if (inReach && far >= 0) descend(s, e, d, px, target);
  else if (hexDist(worldToHex(e.x, e.y), home) > 1) descend(s, e, field(s, [nodeCell(s, m, homeNode)]), px);
  else e.anim = 'idle';
  return true;
};

/** Where a pack's nightmares stand at first: its crossing and the corridors out of it. */
export const packSpots = (L: MazeLayout, home: number): Hex[] => {
  const n = L.nodes[home] as Hex;
  const out: Hex[] = [n];
  for (const e of L.edges) if (e.kind === 'path' && (e.a === home || e.b === home)) out.push({ q: e.q, r: e.r });
  return out;
};

/** A run never outlasts its torch (plus the wait before it starts). */
export const mazeMaxTicks = M.torchTicks + 600;

/**
 * Nobody walks through the Furrows' walls: a hero chasing in a straight line, or a nightmare cutting a corner,
 * that ends a tick inside a wall goes back to where it last stood on the floor.
 */
export const mazeKeep = (s: BattleState, m: Maze): void => {
  const ok: Record<string, [number, number]> = {};
  const keep = (key: string, o: { x: number; y: number }): boolean => {
    if (heroWalkable(s, cellAtWorld(s, o.x, o.y))) {
      ok[key] = [o.x, o.y];
      return false;
    }
    let was = m.ok[key];
    if (!was) {
      // New this tick (a slime's half thrown against a wall): onto the nearest floor next to it.
      const R = s.map.radius;
      const hx = worldToHex(o.x, o.y);
      for (const dd of DIRS) {
        const n = cellId(R, hx.q + dd.q, hx.r + dd.r);
        if (n < 0 || !heroWalkable(s, n)) continue;
        const c = cellCenter(R, n);
        was = [c.x, c.y];
        break;
      }
    }
    if (was) {
      o.x = was[0];
      o.y = was[1];
      ok[key] = was;
    }
    return true;
  };
  // A hero bumped back keeps walking: it finds its way again from where it stands.
  for (const h of s.heroes) if (keep(`h${h.id}`, h) && h.moving) orderMove(s, h, h.destX, h.destY);
  for (const e of s.enemies) if (e.hp > 0) keep(`e${e.id}`, e);
  m.ok = ok;
};
